import { useState, CSSProperties } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../services/api'
import { useCurator } from '../contexts/CuratorContext'
import { LinkText } from '../lib/Linkify'

export type CommentTarget =
  | 'WORKOUT_EXERCISE'
  | 'WORKOUT_ENTRY'
  | 'DIARY_ENTRY'
  | 'DIARY_MEAL'
  | 'MEASUREMENT'
  | 'PROGRESS_PHOTO'

export interface CuratorCommentDto {
  id: string
  text: string
  createdAt: string | Date
  updatedAt: string | Date
  curator?: { id: string; name?: string | null } | null
  curatorId?: string
  /** #230/#237: автор комментария — CURATOR (по умолчанию) или CLIENT. */
  authorType?: 'CURATOR' | 'CLIENT' | string
  isUnread?: boolean
  // Тип цели — нужен, когда несколько веток висят на одной записи дня
  // (питание = DIARY_ENTRY, показатели = DIARY_METRICS).
  targetType?: string
  /** #233: подпись-контекст (напр. название упражнения) — для сводного списка тренировки. */
  contextLabel?: string
  /** #233: только для чтения (комменты упражнений/старые заметки в сводном списке — без edit). */
  readOnly?: boolean
}

interface Props {
  targetType: CommentTarget
  targetId: string
  comments: CuratorCommentDto[]
  invalidateKeys?: Array<string | Array<string | number | undefined>>
  compact?: boolean
  title?: string
}

// Тип-эмодзи чипа-контекста: 🍔 питание, 🏋️ тренировка, 📏 замеры, 📸 фото —
// чтобы сразу видно, к чему коммент (раньше был захардкожен 🏋️ везде).
function commentTypeEmoji(targetType?: string): string {
  switch (targetType) {
    case 'DIARY_ENTRY':
    case 'DIARY_MEAL':
    case 'DIARY_METRICS':
      return '🍔'
    case 'WORKOUT_ENTRY':
    case 'WORKOUT_EXERCISE':
      return '🏋️'
    case 'MEASUREMENT':
      return '📏'
    case 'PROGRESS_PHOTO':
      return '📸'
    default:
      return '💬'
  }
}

function initial(name?: string | null): string {
  if (!name) return 'К'
  return name.trim().charAt(0).toUpperCase() || 'К'
}

function formatDate(d: string | Date): string {
  const date = typeof d === 'string' ? new Date(d) : d
  const now = new Date()
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  if (sameDay) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

export function CuratorCommentThread({
  targetType,
  targetId,
  comments,
  invalidateKeys,
  compact = false,
  title,
}: Props) {
  const queryClient = useQueryClient()
  const { viewingClientId, isAdminView } = useCurator()
  // Админ «за пользователя» (только чтение) — не куратор: кураторского токена у
  // него нет, а сервер режет любой его не-GET 403. Он видит тред глазами
  // клиента, а отправка отвечает тостом, как guardReadOnly на экране программы.
  const canEdit = !!viewingClientId && !isAdminView // куратор (смотрит клиента) — может писать/редактировать
  // #230/#237: клиент тоже может писать в тред (двусторонний) — по упражнению/тренировке/питанию.
  const isClient = !canEdit
  const clientCanPost = isClient && (targetType === 'WORKOUT_EXERCISE' || targetType === 'WORKOUT_ENTRY' || targetType === 'DIARY_ENTRY')
  const canPost = canEdit || clientCanPost
  const guardReadOnly = (fn: () => void) => () => {
    if (!isAdminView) return fn()
    toast('Режим просмотра', { description: 'Вы смотрите аккаунт клиента — изменения недоступны.' })
  }
  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')

  const invalidate = () => {
    const keys = invalidateKeys ?? [['diary'], ['workout'], ['measurements'], ['photos']]
    for (const key of keys) {
      queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })
    }
  }

  const createMutation = useMutation({
    mutationFn: () => canEdit
      ? api.createCuratorComment({ targetType, targetId, text: draft.trim() })
      : api.postClientComment({ targetType, targetId, text: draft.trim() }),
    onSuccess: () => {
      setDraft('')
      invalidate()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => api.updateCuratorComment(id, text),
    onSuccess: () => {
      setEditingId(null)
      setEditingText('')
      invalidate()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCuratorComment(id),
    onSuccess: invalidate,
  })

  const hasComments = comments && comments.length > 0
  if (!canPost && !hasComments) return null

  // Автор пузыря: клиентский коммент — «Вы» (у клиента) / «Клиент» (у куратора);
  // иначе — куратор.
  const authorLabel = (c: CuratorCommentDto): string =>
    c.authorType === 'CLIENT'
      ? (viewingClientId ? 'Клиент' : 'Вы')
      : (c.curator?.name ?? 'Куратор')

  const containerStyle: CSSProperties = {
    marginTop: compact ? 8 : 12,
    padding: compact ? 10 : 12,
    borderRadius: 12,
    background: 'rgba(74,108,247,0.06)',
    border: '1px solid rgba(74,108,247,0.18)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  }

  const bubbleStyle: CSSProperties = {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
  }

  const avatarStyle: CSSProperties = {
    width: 24,
    height: 24,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)',
    color: 'white',
    fontSize: 11,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }

  const textStyle: CSSProperties = {
    fontSize: compact ? 12 : 13,
    lineHeight: 1.4,
    color: '#1A3A5C',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  }

  return (
    <div style={containerStyle}>
      {title && (
        <div style={{ fontSize: compact ? 11 : 12, fontWeight: 600, color: '#4A6CF7', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {title}
        </div>
      )}

      {hasComments && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {comments.map((c) => {
            const isEditing = editingId === c.id
            return (
              <div key={c.id} style={bubbleStyle}>
                <div style={{ ...avatarStyle, ...(c.authorType === 'CLIENT' ? { background: 'linear-gradient(135deg, #1B9E44, #34C759)' } : {}) }}>{initial(authorLabel(c))}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1A3A5C' }}>
                      {authorLabel(c)}
                    </span>
                    <span style={{ fontSize: 10, color: '#8896A7' }}>{formatDate(c.createdAt)}</span>
                    {c.isUnread && !canEdit && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
                        color: 'white', background: '#FF3B30',
                        padding: '2px 6px', borderRadius: 8, textTransform: 'uppercase',
                      }}>
                        Новый
                      </span>
                    )}
                  </div>
                  {c.contextLabel && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#6C5CE7', background: 'rgba(108,92,231,0.10)', borderRadius: 8, padding: '1px 7px', marginBottom: 4 }}>
                      {commentTypeEmoji(c.targetType)} {c.contextLabel}
                    </div>
                  )}
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={2}
                        style={{
                          width: '100%',
                          resize: 'vertical',
                          fontSize: 13,
                          padding: 8,
                          borderRadius: 8,
                          border: '1px solid rgba(74,108,247,0.3)',
                          fontFamily: 'inherit',
                          background: 'white',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          disabled={!editingText.trim() || updateMutation.isPending}
                          onClick={() => updateMutation.mutate({ id: c.id, text: editingText.trim() })}
                          style={commentBtnStyle('primary')}
                        >
                          Сохранить
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null)
                            setEditingText('')
                          }}
                          style={commentBtnStyle('ghost')}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={textStyle}><LinkText>{c.text}</LinkText></div>
                  )}
                  {canEdit && !isEditing && !c.readOnly && c.curator?.id && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(c.id)
                          setEditingText(c.text)
                        }}
                        style={linkBtnStyle}
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm('Удалить комментарий?')) deleteMutation.mutate(c.id)
                        }}
                        style={{ ...linkBtnStyle, color: '#D14343' }}
                      >
                        Удалить
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {canPost && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={canEdit ? (hasComments ? 'Добавить комментарий…' : 'Оставить комментарий клиенту…') : (hasComments ? 'Ответить…' : 'Написать куратору…')}
            rows={1}
            style={{
              flex: 1,
              minHeight: 36,
              maxHeight: 120,
              resize: 'vertical',
              fontSize: 13,
              padding: 8,
              borderRadius: 8,
              border: '1px solid rgba(74,108,247,0.3)',
              fontFamily: 'inherit',
              background: 'white',
            }}
          />
          <button
            type="button"
            data-testid="comment-thread-send"
            disabled={!draft.trim() || createMutation.isPending}
            onClick={guardReadOnly(() => createMutation.mutate())}
            style={commentBtnStyle('primary')}
          >
            {createMutation.isPending ? '…' : 'Отпр.'}
          </button>
        </div>
      )}
    </div>
  )
}

function commentBtnStyle(variant: 'primary' | 'ghost'): CSSProperties {
  if (variant === 'primary') {
    return {
      padding: '6px 12px',
      borderRadius: 8,
      border: 'none',
      background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)',
      color: 'white',
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
    }
  }
  return {
    padding: '6px 12px',
    borderRadius: 8,
    border: '1px solid rgba(74,108,247,0.3)',
    background: 'transparent',
    color: '#4A6CF7',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  }
}

const linkBtnStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: '#4A6CF7',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
}
